import { SHORT_DISCLAIMER } from "@netkeep/legal";
import { useEffect } from "react";
import { Link, NavLink, Outlet, ScrollRestoration, useLocation } from "react-router";
import { SITE_URL } from "@/lib/config";
import { trackPageview } from "@/lib/analytics";
import { applyUpdate, usePwa } from "@/lib/pwa";
import { PLATFORM_PAGES, seoFor } from "@/lib/seo";
import { TABS } from "@/lib/tabs";
import { Icon } from "./Icon";
import { LegalLinks } from "./LegalLinks";
import { Button } from "./ui";

/** Updates (or adds) the head element whose `key` attribute is `keyValue`. */
function setHeadTag(tag: "meta" | "link", key: string, keyValue: string, attr: string, value: string) {
  let el = document.head.querySelector(`${tag}[${key}="${keyValue}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(key, keyValue);
    document.head.append(el);
  }
  el.setAttribute(attr, value);
}

function NavLinks() {
  return (
    <>
      {TABS.map((tab) => (
        <NavLink key={tab.path} to={tab.path} end className="nav__link">
          <Icon name={tab.icon} />
          <span>{tab.title}</span>
        </NavLink>
      ))}
    </>
  );
}

/** Top bar with tabs on wide screens; bottom tab bar on phones (like the mobile app). */
export function AppShell() {
  const { pathname } = useLocation();
  const { updateReady } = usePwa();

  useEffect(() => trackPageview(pathname), [pathname]);

  // Keep the head in step with the URL (the build prerenders the same tags per page).
  useEffect(() => {
    const page = seoFor(pathname) ?? seoFor("/")!;
    // Legal pages set their own title.
    if (pathname !== "/terms" && pathname !== "/privacy" && pathname !== "/delete-data") document.title = page.title;
    setHeadTag("meta", "name", "description", "content", page.description);
    setHeadTag("link", "rel", "canonical", "href", `${SITE_URL}${page.path}`);
    setHeadTag("meta", "name", "robots", "content", page.index ? "index, follow" : "noindex");
  }, [pathname]);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <div className="topbar__inner">
          <Link to="/" className="logo" aria-label="NetKeeps home">
            Net<span>Keeps</span>
          </Link>
          <nav className="nav" aria-label="Main">
            <NavLinks />
          </nav>
        </div>
      </header>
      <div className="page">
        <main id="main" className="page__main">
          {updateReady ? (
            <div className="update-bar" role="status">
              <span className="grow">A new version of NetKeep is ready.</span>
              <Button title="Reload" variant="secondary" onClick={applyUpdate} />
            </div>
          ) : null}
          <Outlet />
        </main>
        <footer className="footer">
          <p>{SHORT_DISCLAIMER}</p>
          <p>Not affiliated with or endorsed by any marketplace or payment provider.</p>
          <nav className="legal-links" aria-label="Fee calculators">
            {PLATFORM_PAGES.map((page) => (
              <Link key={page.path} to={page.path} className="link">
                {page.heading}
              </Link>
            ))}
          </nav>
          <LegalLinks />
        </footer>
      </div>
      <nav className="tabbar" aria-label="Main">
        <NavLinks />
      </nav>
      <ScrollRestoration />
    </>
  );
}
